import React, { useState, useEffect, useCallback } from "react";
import { X, Plus, Trash2, RefreshCw, Copy, Check, Building2, Smartphone, Activity, Loader2, Link2 } from "lucide-react";
import { API } from "../lib/api";
import { nexusListSources, nexusAddSource, nexusDelSource, nexusSyncSource, nexusFeed } from "../lib/api";
import { toast } from "sonner";

const INGEST_URL = `${API}/nexus/ingest`;

function CopyBtn({ text, label = "Copy", testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1600); }
    catch { toast.error("Copy failed — long-press to copy"); }
  };
  return (
    <button onClick={copy} data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg nx-mono text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] transition-colors">
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {done ? "Copied" : label}
    </button>
  );
}

function businessPrompt(url, secret) {
  const endpoint = url ? url : "https://YOUR-APP-URL/api/nexus-feed";
  return `Add a read-only "Nexus feed" API endpoint to this app so my personal assistant Nexus can see live business progress.

Requirements:
1. Create a GET endpoint at /api/nexus-feed
2. It must require a secret key. Accept it as a query param ?key=... and reject with 403 if it does not exactly equal: ${secret}
3. When the key is valid, return a JSON object (Content-Type: application/json) with this shape:
   {
     "summary": "One-line plain-English status, e.g. 'Today: 12 orders, $3,240 revenue, 4 new customers'",
     "metrics": {
       "revenue_today": 0,
       "revenue_month": 0,
       "orders_today": 0,
       "new_customers_today": 0,
       "pending_tasks": 0
     },
     "updated_at": "ISO timestamp"
   }
   Fill these with REAL numbers computed from the app's database. Only include metrics that make sense for this app; keep "summary" always present and human-readable.
4. Do not require any login/session for this endpoint — the secret key is the only auth. Keep it read-only (no writes).
5. Make sure it works when called from another server (enable CORS or it is server-to-server, so CORS is fine).

After you build it, the public URL will be: ${endpoint}`;
}

function shortcutBody(sourceId, secret) {
  return JSON.stringify({
    source_id: sourceId,
    key: secret,
    title: "Text from Mom",
    summary: "Mom texted: 'Call me when you're free'",
    kind: "phone",
    data: { type: "message", from: "Mom" }
  }, null, 2);
}

export default function NexusConnections({ session, onClose }) {
  const [tab, setTab] = useState("business");
  const [sources, setSources] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addName, setAddName] = useState("");
  const [addUrl, setAddUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([nexusListSources(session.user_id), nexusFeed(session.user_id)]);
      setSources(s || []); setFeed(f || []);
    } catch (e) { toast.error("Could not load connections"); }
    finally { setLoading(false); }
  }, [session.user_id]);

  useEffect(() => { load(); }, [load]);

  const business = sources.filter((s) => s.kind === "business");
  const phone = sources.find((s) => s.kind === "phone");

  const addBusiness = async () => {
    if (!addName.trim() || !addUrl.trim()) { toast.error("Enter a name and the app's feed URL"); return; }
    setBusy(true);
    try {
      await nexusAddSource({ user_id: session.user_id, name: addName.trim(), kind: "business", mode: "pull", url: addUrl.trim() });
      setAddName(""); setAddUrl(""); toast.success("App connected"); await load();
    } catch (e) { toast.error("Could not add app"); } finally { setBusy(false); }
  };

  const createPhone = async () => {
    setBusy(true);
    try {
      await nexusAddSource({ user_id: session.user_id, name: "My iPhone", kind: "phone", mode: "push", url: "" });
      toast.success("Phone bridge ready"); await load();
    } catch (e) { toast.error("Could not create bridge"); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await nexusDelSource(id, session.user_id); await load(); toast.success("Removed"); }
    catch (e) { toast.error("Could not remove"); }
  };

  const sync = async (id) => {
    setSyncing(id);
    try {
      const r = await nexusSyncSource(id, session.user_id);
      if (r.ok) toast.success("Synced — Nexus has the latest");
      else toast.error(r?.source?.last_error || "Sync failed — check the URL & key");
      await load();
    } catch (e) { toast.error("Sync failed"); } finally { setSyncing(null); }
  };

  const inp = "w-full bg-[#030712] text-cyan-50 border border-[#00F0FF44] rounded-xl px-3 py-2 nx-mono text-sm focus:outline-none focus:border-[#00F0FF]";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 nx-display" data-testid="nexus-connections-modal">
      <div className="nx-glass rounded-3xl w-full max-w-xl max-h-[92vh] overflow-y-auto p-6 space-y-5 relative">
        <button onClick={onClose} data-testid="nexus-connections-close" className="absolute top-4 right-4 text-cyan-200/60 hover:text-white"><X className="w-6 h-6" /></button>
        <h2 className="nx-orbitron text-xl font-black tracking-widest text-[#00F0FF] flex items-center gap-2"><Link2 className="w-5 h-5" /> CONNECT NEXUS</h2>
        <p className="text-cyan-100/60 text-xs nx-mono -mt-2">Feed your business apps and your phone into Nexus so he sees everything in real time.</p>

        <div className="flex gap-2">
          <button onClick={() => setTab("business")} data-testid="nexus-tab-business"
            className={`flex-1 py-2 rounded-xl nx-mono text-xs font-bold flex items-center justify-center gap-1.5 border transition-colors ${tab === "business" ? "bg-[#00F0FF] text-[#030712] border-[#00F0FF]" : "text-cyan-200/70 border-cyan-200/25"}`}>
            <Building2 className="w-4 h-4" /> BUSINESS APPS
          </button>
          <button onClick={() => setTab("phone")} data-testid="nexus-tab-phone"
            className={`flex-1 py-2 rounded-xl nx-mono text-xs font-bold flex items-center justify-center gap-1.5 border transition-colors ${tab === "phone" ? "bg-[#00F0FF] text-[#030712] border-[#00F0FF]" : "text-cyan-200/70 border-cyan-200/25"}`}>
            <Smartphone className="w-4 h-4" /> PHONE BRIDGE
          </button>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : tab === "business" ? (
          <div className="space-y-4">
            <div className="space-y-2 bg-[#0a1224] rounded-2xl p-4">
              <p className="text-cyan-200/70 text-xs nx-mono tracking-widest">CONNECT A BUSINESS APP</p>
              <input className={inp} placeholder="App name (e.g. My Store)" value={addName} onChange={(e) => setAddName(e.target.value)} data-testid="nexus-src-name" />
              <input className={inp} placeholder="Feed URL (e.g. https://mystore.com/api/nexus-feed)" value={addUrl} onChange={(e) => setAddUrl(e.target.value)} data-testid="nexus-src-url" />
              <button onClick={addBusiness} disabled={busy} data-testid="nexus-src-add"
                className="w-full py-2.5 rounded-xl nx-orbitron font-bold tracking-widest uppercase text-sm bg-[#00F0FF] text-[#030712] disabled:opacity-40 flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> Connect App
              </button>
              <p className="text-cyan-100/40 text-[11px] nx-mono leading-relaxed">
                Don't have that URL yet? Add the app first, then use the "Copy setup prompt" button below and paste it into that app's builder — it'll create the feed endpoint for you.
              </p>
            </div>

            {business.length === 0 && <p className="text-center text-cyan-100/40 text-sm nx-mono py-4">No apps connected yet.</p>}
            {business.map((s) => (
              <div key={s.id} className="bg-[#0a1224] rounded-2xl p-4 space-y-2" data-testid={`nexus-src-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-cyan-50 font-semibold truncate">{s.name}</p>
                    <p className="text-cyan-100/40 text-[11px] nx-mono truncate">{s.url}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => sync(s.id)} disabled={syncing === s.id} data-testid={`nexus-src-sync-${s.id}`}
                      className="p-2 rounded-lg text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11]">
                      {syncing === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </button>
                    <button onClick={() => del(s.id)} data-testid={`nexus-src-del-${s.id}`} className="p-2 rounded-lg text-red-400 border border-red-400/30 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px] nx-mono">
                  <span className={`px-2 py-0.5 rounded-full ${s.last_status === "ok" ? "bg-emerald-500/20 text-emerald-300" : s.last_status === "error" ? "bg-red-500/20 text-red-300" : "bg-cyan-500/10 text-cyan-300/70"}`}>
                    {s.last_status === "ok" ? "● live" : s.last_status === "error" ? "● error" : "○ not synced"}
                  </span>
                  {s.last_error && <span className="text-red-300/80 truncate max-w-[200px]">{s.last_error}</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <CopyBtn text={businessPrompt(s.url, s.secret)} label="Copy setup prompt" testid={`nexus-src-prompt-${s.id}`} />
                  <CopyBtn text={s.secret} label="Copy key" testid={`nexus-src-key-${s.id}`} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {!phone ? (
              <div className="bg-[#0a1224] rounded-2xl p-5 text-center space-y-3">
                <Smartphone className="w-10 h-10 mx-auto text-[#00F0FF]" />
                <p className="text-cyan-50 font-semibold">Set up your iPhone bridge</p>
                <p className="text-cyan-100/50 text-xs nx-mono leading-relaxed">
                  Creates a private, secure address your iPhone Shortcuts send life data to — texts from key people, spending, reminders, health stats — so Nexus keeps learning about you automatically.
                </p>
                <button onClick={createPhone} disabled={busy} data-testid="nexus-phone-create"
                  className="w-full py-2.5 rounded-xl nx-orbitron font-bold tracking-widest uppercase text-sm bg-[#00F0FF] text-[#030712] disabled:opacity-40 flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" /> Create Phone Bridge
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#0a1224] rounded-2xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-cyan-50 font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4 text-[#00F0FF]" /> {phone.name}</p>
                    <button onClick={() => del(phone.id)} data-testid="nexus-phone-del" className="p-2 rounded-lg text-red-400 border border-red-400/30 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div>
                    <p className="text-cyan-200/60 text-[11px] nx-mono tracking-widest mb-1">POST URL</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-[#030712] rounded-lg px-2 py-1.5 text-[11px] text-cyan-300 nx-mono break-all">{INGEST_URL}</code>
                      <CopyBtn text={INGEST_URL} label="Copy" testid="nexus-phone-url-copy" />
                    </div>
                  </div>
                  <div>
                    <p className="text-cyan-200/60 text-[11px] nx-mono tracking-widest mb-1">JSON BODY TEMPLATE</p>
                    <pre className="bg-[#030712] rounded-lg p-2 text-[10px] text-cyan-300/90 nx-mono overflow-x-auto whitespace-pre-wrap">{shortcutBody(phone.id, phone.secret)}</pre>
                    <div className="mt-2"><CopyBtn text={shortcutBody(phone.id, phone.secret)} label="Copy body template" testid="nexus-phone-body-copy" /></div>
                  </div>
                </div>

                <div className="bg-[#0a1224] rounded-2xl p-4 space-y-2">
                  <p className="text-cyan-200/70 text-xs nx-mono tracking-widest">📱 APPLE SHORTCUTS — HANDS-FREE SETUP</p>
                  <ol className="text-cyan-100/70 text-xs nx-mono leading-relaxed list-decimal pl-4 space-y-1">
                    <li>Open the <b>Shortcuts</b> app → <b>Automation</b> tab → <b>＋</b> → <b>Create Personal Automation</b>.</li>
                    <li>Pick a trigger, e.g. <b>Message</b> → "When I get a text from Mom", or <b>Arrive</b> / <b>Time of Day</b> for daily summaries.</li>
                    <li>Add action <b>Get Contents of URL</b>. Set URL to the POST URL above, Method = <b>POST</b>, Request Body = <b>JSON</b>.</li>
                    <li>Paste the JSON fields above. Keep <b>source_id</b> and <b>key</b> exactly as shown; set <b>summary</b> to the info you want Nexus to learn (you can use Shortcut variables like the message text).</li>
                    <li>Turn <b>OFF</b> "Ask Before Running" so it fires automatically. Done — Nexus now learns from your phone.</li>
                  </ol>
                  <p className="text-amber-300/70 text-[11px] nx-mono">Note: Apple blocks reading call audio and raw texts directly, so Shortcuts is the automatic, Apple-approved way to feed Nexus.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* live feed preview */}
        <div className="space-y-2">
          <p className="text-cyan-200/60 text-xs nx-mono tracking-widest flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> WHAT NEXUS SEES ({feed.length})</p>
          {feed.length === 0 ? (
            <p className="text-cyan-100/35 text-xs nx-mono py-2">Nothing yet — connect an app or sync to populate this.</p>
          ) : (
            <div className="max-h-40 overflow-y-auto space-y-1">
              {feed.map((it) => (
                <div key={it.id} className="bg-[#0a1224] rounded-lg px-3 py-1.5 text-xs text-cyan-50" data-testid="nexus-feed-item">
                  <span className={`nx-mono text-[10px] mr-2 ${it.kind === "business" ? "text-emerald-300" : "text-cyan-300"}`}>[{(it.kind || "").toUpperCase()}]</span>
                  <span className="text-cyan-200/60">{it.source_name}:</span> {it.summary}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
